import React, { useCallback, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faSpinner,
  faXmark,
  faCopy,
  faCheck,
  faRotate,
  faTrash,
  faFloppyDisk,
  faTv,
} from '@fortawesome/free-solid-svg-icons'
import {
  kioskDevicesService,
  type KioskDeviceRecord,
} from '../../services/kioskDevicesService'

type SecretReveal = {
  title: string
  deviceKey: string
  warning: string
} | null

type PermissionLevelOption = {
  name: string
}

function formatTs(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return '—'
  }
}

type Props = {
  permissionLevels: PermissionLevelOption[]
}

const KioskDevicesPanel: React.FC<Props> = ({ permissionLevels }) => {
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [devices, setDevices] = useState<KioskDeviceRecord[]>([])
  const [secretReveal, setSecretReveal] = useState<SecretReveal>(null)
  const [copied, setCopied] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createAccessLevel, setCreateAccessLevel] = useState('')
  const [creating, setCreating] = useState(false)
  const [drafts, setDrafts] = useState<Record<string, { name: string; accessLevel: string }>>({})

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await kioskDevicesService.list()
      setDevices(list)
      const next: Record<string, { name: string; accessLevel: string }> = {}
      for (const d of list) {
        next[d.id] = { name: d.name, accessLevel: d.accessLevel }
      }
      setDrafts(next)
    } catch (e: any) {
      setError(e?.message || 'Failed to load kiosk devices')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!createAccessLevel && permissionLevels.length > 0) {
      setCreateAccessLevel(permissionLevels[0].name)
    }
  }, [permissionLevels, createAccessLevel])

  const isDirty = (id: string) => {
    const d = devices.find((x) => x.id === id)
    const draft = drafts[id]
    if (!d || !draft) return false
    return draft.name !== d.name || draft.accessLevel !== d.accessLevel
  }

  const saveRow = async (id: string) => {
    const draft = drafts[id]
    if (!draft) return
    setSavingId(id)
    setError(null)
    try {
      const updated = await kioskDevicesService.update({
        id,
        name: draft.name.trim(),
        accessLevel: draft.accessLevel,
      })
      setDevices((prev) => prev.map((d) => (d.id === id ? updated : d)))
      setDrafts((prev) => ({
        ...prev,
        [id]: { name: updated.name, accessLevel: updated.accessLevel },
      }))
    } catch (e: any) {
      setError(e?.message || 'Failed to update device')
    } finally {
      setSavingId(null)
    }
  }

  const toggleEnabled = async (device: KioskDeviceRecord) => {
    setSavingId(device.id)
    setError(null)
    try {
      const updated = await kioskDevicesService.update({
        id: device.id,
        enabled: !device.enabled,
      })
      setDevices((prev) => prev.map((d) => (d.id === device.id ? updated : d)))
    } catch (e: any) {
      setError(e?.message || 'Failed to update device')
    } finally {
      setSavingId(null)
    }
  }

  const rotate = async (id: string) => {
    if (!window.confirm('Rotate this device key? The current key will stop working immediately.')) return
    setSavingId(id)
    setError(null)
    try {
      const result = await kioskDevicesService.rotate(id)
      setDevices((prev) => prev.map((d) => (d.id === id ? result.device : d)))
      setSecretReveal({
        title: 'Device key rotated',
        deviceKey: result.deviceKey,
        warning: result.warning,
      })
      setCopied(false)
    } catch (e: any) {
      setError(e?.message || 'Failed to rotate device key')
    } finally {
      setSavingId(null)
    }
  }

  const remove = async (id: string) => {
    if (!window.confirm('Delete this kiosk device permanently? Outstanding sessions will stop working.')) return
    setSavingId(id)
    setError(null)
    try {
      await kioskDevicesService.delete(id)
      setDevices((prev) => prev.filter((d) => d.id !== id))
      setDrafts((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    } catch (e: any) {
      setError(e?.message || 'Failed to delete device')
    } finally {
      setSavingId(null)
    }
  }

  const create = async () => {
    const name = createName.trim()
    if (!name || !createAccessLevel) return
    setCreating(true)
    setError(null)
    try {
      const result = await kioskDevicesService.create({
        name,
        accessLevel: createAccessLevel,
      })
      setDevices((prev) => [result.device, ...prev])
      setDrafts((prev) => ({
        ...prev,
        [result.device.id]: { name: result.device.name, accessLevel: result.device.accessLevel },
      }))
      setCreateOpen(false)
      setCreateName('')
      setSecretReveal({
        title: 'Device key created',
        deviceKey: result.deviceKey,
        warning: result.warning,
      })
      setCopied(false)
    } catch (e: any) {
      setError(e?.message || 'Failed to create device')
    } finally {
      setCreating(false)
    }
  }

  const copySecret = async () => {
    if (!secretReveal?.deviceKey) return
    try {
      await navigator.clipboard.writeText(secretReveal.deviceKey)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <FontAwesomeIcon icon={faSpinner} className="text-2xl animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FontAwesomeIcon icon={faTv} />
            Kiosk Devices
          </h2>
          <p className="text-sm text-base-content/70 mt-1 max-w-3xl">
            Enrolled hardware that can mint portal sessions without password or MFA. Assign a
            permission level like a user. The device key is shown only once on create or rotate —
            put it in the kiosk host config.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Create device
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {devices.length === 0 ? (
        <div className="alert">
          <span>No kiosk devices yet. Create one to enroll lobby hardware.</span>
        </div>
      ) : (
        <div className="overflow-x-auto border border-base-300 rounded-lg">
          <table className="table table-sm w-full">
            <thead>
              <tr className="bg-base-200">
                <th>Name</th>
                <th>Prefix</th>
                <th>Permission level</th>
                <th>Status</th>
                <th>Last used</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => {
                const draft = drafts[device.id] || { name: device.name, accessLevel: device.accessLevel }
                const dirty = isDirty(device.id)
                const busy = savingId === device.id
                return (
                  <tr key={device.id} className={!device.enabled ? 'opacity-60' : undefined}>
                    <td>
                      <input
                        className="input input-bordered input-xs w-40"
                        value={draft.name}
                        disabled={busy}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [device.id]: { ...draft, name: e.target.value },
                          }))
                        }
                      />
                    </td>
                    <td>
                      <code className="text-xs">ltskiosk_{device.keyPrefix}_…</code>
                    </td>
                    <td>
                      <select
                        className="select select-bordered select-xs"
                        value={draft.accessLevel}
                        disabled={busy}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [device.id]: { ...draft, accessLevel: e.target.value },
                          }))
                        }
                      >
                        {!permissionLevels.some((l) => l.name === draft.accessLevel) && (
                          <option value={draft.accessLevel}>{draft.accessLevel}</option>
                        )}
                        {permissionLevels.map((l) => (
                          <option key={l.name} value={l.name}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="toggle toggle-xs toggle-success"
                          checked={device.enabled}
                          disabled={busy}
                          onChange={() => void toggleEnabled(device)}
                        />
                        <span className="text-xs">{device.enabled ? 'On' : 'Off'}</span>
                      </label>
                    </td>
                    <td className="text-xs whitespace-nowrap opacity-70">{formatTs(device.lastUsedAt)}</td>
                    <td>
                      <div className="flex flex-wrap gap-1 justify-end">
                        <button
                          type="button"
                          className={`btn btn-xs ${dirty ? 'btn-primary' : 'btn-ghost'}`}
                          disabled={!dirty || busy}
                          onClick={() => void saveRow(device.id)}
                          title="Save name / permission level"
                        >
                          {busy ? (
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                          ) : (
                            <FontAwesomeIcon icon={faFloppyDisk} />
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-xs btn-ghost"
                          disabled={busy}
                          onClick={() => void rotate(device.id)}
                          title="Rotate device key"
                        >
                          <FontAwesomeIcon icon={faRotate} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-xs btn-ghost text-error"
                          disabled={busy}
                          onClick={() => void remove(device.id)}
                          title="Delete device"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg">Create kiosk device</h3>
            <div className="form-control mt-4">
              <label className="label">
                <span className="label-text">Name</span>
              </label>
              <input
                className="input input-bordered"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Lobby TV"
                autoFocus
              />
            </div>
            <div className="form-control mt-3">
              <label className="label">
                <span className="label-text">Permission level</span>
              </label>
              <select
                className="select select-bordered"
                value={createAccessLevel}
                onChange={(e) => setCreateAccessLevel(e.target.value)}
              >
                {permissionLevels.map((l) => (
                  <option key={l.name} value={l.name}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="modal-action">
              <button type="button" className="btn btn-ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={creating || !createName.trim() || !createAccessLevel}
                onClick={() => void create()}
              >
                {creating ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Create'}
              </button>
            </div>
          </div>
          <button type="button" className="modal-backdrop" aria-label="Close" onClick={() => setCreateOpen(false)} />
        </div>
      )}

      {secretReveal && (
        <div className="modal modal-open">
          <div className="modal-box max-w-xl">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-bold text-lg">{secretReveal.title}</h3>
              <button type="button" className="btn btn-sm btn-circle btn-ghost" onClick={() => setSecretReveal(null)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <p className="text-sm text-warning mt-2">{secretReveal.warning}</p>
            <pre className="mt-4 p-3 bg-base-200 rounded-lg text-xs break-all whitespace-pre-wrap">
              {secretReveal.deviceKey}
            </pre>
            <div className="modal-action">
              <button type="button" className="btn btn-primary" onClick={() => void copySecret()}>
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="mr-2" />
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button type="button" className="btn" onClick={() => setSecretReveal(null)}>
                Done
              </button>
            </div>
          </div>
          <button type="button" className="modal-backdrop" aria-label="Close" onClick={() => setSecretReveal(null)} />
        </div>
      )}
    </div>
  )
}

export default KioskDevicesPanel
